/**
 * A-09 — admin refunds (D-05: refunds are given from here only).
 *
 * 1. Patients' cancel-and-refund requests (ORD-16): a paid order the patient asked to cancel before
 *    dispatch. Refund it — by default (paid − GST) × the refundable share; less is allowed, never
 *    more (D-14) — with notes and an optional reference document, or decline it with a note the
 *    patient sees. While a request is open the pharmacist can't move the order on.
 *
 *    Built for volume: status tabs with live counts; a server-side search (order reference, patient
 *    name or phone), order (oldest / newest first) and one patient's requests; a virtual table that
 *    loads the next page as it scrolls. The view lives in the URL (?status=&q=&sort=&patient=
 *    &request=), so it can be reloaded or shared. A request opens in a dialog with what deciding it
 *    needs — the order, its payment and refunds, the patient's other requests — and Previous / Next
 *    to work through the queue.
 * 2. "Refund an order": look up an order and refund it directly — e.g. a payment captured after the
 *    order was cancelled. Completion lands via the Razorpay refund.processed webhook.
 */

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { toast } from "sonner";

import { ApiError, describeError } from "../../api/errors";
import {
  attachRefundReference,
  countCancelRequests,
  declineCancelRequest,
  downloadRefundReference,
  getCancelRequest,
  initiateRefund,
  listCancelRequests,
  listRefunds,
  lookupOrder,
  refundCancelRequest,
  type AdminCancelRequest,
  type AdminCancelRequestDetail,
  type CancelRequestCounts,
  type CancelRequestQuery,
  type CancelRequestStatus,
  type Refund,
} from "../../api/payments";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import { ExportButton } from "../../components/ui/ExportButton";
import { FilterBar } from "../../components/ui/FilterBar";
import { Modal } from "../../components/ui/Modal";
import { Select } from "../../components/ui/Select";
import { TableRow, VirtualTable } from "../../components/ui/VirtualTable";
import { formatINR } from "../../lib/money";
import { phoneLabel } from "../../lib/phone";
import type { TableColumn } from "../../lib/tableColumns";
import {
  byPageNumber,
  EXPORT_PAGE_SIZE,
  sheetDateTime,
  sheetLabel,
  sheetPhone,
  type ExportColumn,
} from "../../lib/tableExport";
import { useSearchBox, useUrlParams } from "../../lib/urlState";
import { useNow } from "../../lib/useNow";

const REFERENCE_ACCEPT = "application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp";

// The API's limits on a note (cancel_request.Note) and on the search term.
const NOTE_MIN = 3;
const NOTE_MAX = 1000;
const SEARCH_MAX = 100;

const PER_PAGE = 30;
const ROW_H = 60;
// An open request older than this is flagged in the list: the order has been on hold that long.
const OVERDUE_MS = 24 * 60 * 60_000;

/** The list and its counts share this prefix, so one invalidation refreshes both. */
const LIST_KEY = ["admin-cancel-requests"] as const;
const detailKey = (id: string) => ["admin-cancel-request", id] as const;

/** The admin-facing wording for the API's refund refusals. */
function refundError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "ORDER_NOT_REFUNDABLE") return "This order has no captured payment to refund.";
    if (e.code === "ALREADY_REFUNDED") return "This payment has already been refunded as far as it can be.";
    if (e.code === "CANCEL_REQUEST_RESOLVED") return "Someone has already dealt with this request.";
  }
  return describeError(e);
}

/** Attach the reference after the refund went through; the refund stands even if this fails. */
async function attachAfterRefund(refundId: string | null, file: File | null): Promise<void> {
  if (!refundId || !file) return;
  try {
    await attachRefundReference(refundId, file);
  } catch (e) {
    toast.error(`The refund was started, but the reference couldn't be attached: ${describeError(e)}`);
  }
}

/** After a decision (or a refusal because someone else decided first): re-read what changed. */
function refreshAfterDecision(qc: QueryClient, r: { id: string; order_id: string }) {
  void qc.invalidateQueries({ queryKey: LIST_KEY });
  void qc.invalidateQueries({ queryKey: detailKey(r.id) });
  void qc.invalidateQueries({ queryKey: ["admin-refunds", r.order_id] });
}

// ---------------------------------------------------------------- formatting

function dateTime(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  });
}

/** How long ago, roughly: "5 min", "3 h", "2 d". */
function age(iso: string, now: number): string {
  const mins = Math.max(1, Math.floor((now - new Date(iso).getTime()) / 60_000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

const ago = (iso: string, now: number) => `${age(iso, now)} ago`;

const isOverdue = (r: AdminCancelRequest, now: number) =>
  r.status === "open" && now - new Date(r.created_at).getTime() > OVERDUE_MS;

const STATUS_LABEL: Record<CancelRequestStatus, string> = {
  open: "To decide",
  refunded: "Refunded",
  declined: "Declined",
};
const STATUS_TONE: Record<CancelRequestStatus, string> = {
  open: "bg-amber-50 text-amber-700",
  refunded: "bg-emerald-50 text-emerald-700",
  declined: "bg-gray-100 text-gray-600",
};
const REFUND_LABEL: Record<string, string> = {
  initiated: "Processing",
  processed: "Processed",
  failed: "Failed",
};
const REFUND_TONE: Record<string, string> = {
  initiated: "bg-amber-50 text-amber-700",
  processed: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
};

function StatusBadge({ status }: { status: CancelRequestStatus }) {
  return (
    <span className={`inline-flex w-fit shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[status]}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

function RefundPill({ status }: { status: string }) {
  return (
    <span className={`inline-flex w-fit shrink-0 rounded-full px-2 py-0.5 text-xs ${REFUND_TONE[status] ?? "bg-gray-100 text-gray-600"}`}>
      {REFUND_LABEL[status] ?? status}
    </span>
  );
}

// ---------------------------------------------------------------- page

type Tab = CancelRequestStatus | "all";
type Sort = NonNullable<CancelRequestQuery["sort"]>;

const TABS: { key: Tab; label: string }[] = [
  { key: "open", label: "To decide" },
  { key: "refunded", label: "Refunded" },
  { key: "declined", label: "Declined" },
  { key: "all", label: "All" },
];

const parseTab = (v: string | null): Tab => TABS.find((t) => t.key === v)?.key ?? "open";
const parseSort = (v: string | null): Sort | undefined => (v === "oldest" || v === "newest" ? v : undefined);
/** Open requests are first come, first served; the rest show the latest first. */
const defaultSort = (tab: Tab): Sort => (tab === "open" ? "oldest" : "newest");

const EXPORT_COLUMNS: ExportColumn<AdminCancelRequest>[] = [
  { header: "Requested (IST)", value: (r) => sheetDateTime(r.created_at) },
  { header: "Order", value: (r) => r.order_ref },
  { header: "Order status", value: (r) => r.order_status_label },
  { header: "Patient", value: (r) => r.patient_name },
  { header: "Phone", value: (r) => sheetPhone(r.patient_phone) },
  { header: "Reason", value: (r) => r.reason },
  { header: "Paid (₹)", value: (r) => r.amount_paid },
  { header: "GST (₹)", value: (r) => r.gst_total },
  { header: "Refundable now (₹)", value: (r) => r.refund_max },
  { header: "Request", value: (r) => sheetLabel(r.status) },
  { header: "Refunded (₹)", value: (r) => r.refund_amount },
  { header: "Refund status", value: (r) => sheetLabel(r.refund_status) },
  { header: "Decided (IST)", value: (r) => sheetDateTime(r.resolved_at) },
  { header: "Note", value: (r) => r.resolution_note },
];

export function Refunds() {
  const [params, setParam] = useUrlParams();
  const tab = parseTab(params.get("status"));
  const q = params.get("q") ?? "";
  const sort = parseSort(params.get("sort"));
  const patientId = params.get("patient") ?? "";
  const openId = params.get("request");
  const [lookupOpen, setLookupOpen] = useState(false);
  const now = useNow();

  const search = useSearchBox(q, (text) => setParam({ q: text }));

  const filter = useMemo(() => ({ q: q || undefined, patient_id: patientId || undefined }), [q, patientId]);
  const query: CancelRequestQuery = useMemo(
    () => ({ ...filter, status: tab, sort, per_page: PER_PAGE }),
    [filter, tab, sort],
  );

  const counts = useQuery({
    queryKey: [...LIST_KEY, "counts", filter],
    queryFn: () => countCancelRequests(filter),
    placeholderData: keepPreviousData,
    // New requests come in while the page is open: keep the tabs' numbers current.
    refetchInterval: 60_000,
  });

  const list = useInfiniteQuery({
    queryKey: [...LIST_KEY, "list", query],
    queryFn: ({ pageParam }) => listCancelRequests({ ...query, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
    // The previous rows stay while a new search loads, instead of the table blanking.
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(() => {
    // Pages are by offset: a request arriving between two pages shifts the rest by one, so a row
    // can come twice. Keep the first.
    const seen = new Set<string>();
    return (list.data?.pages ?? []).flatMap((p) => p.data).filter((r) => !seen.has(r.id) && !!seen.add(r.id));
  }, [list.data]);
  const total = list.data?.pages[0]?.total;

  // ---- the open request, and Previous / Next through the list
  const detail = useQuery({
    queryKey: detailKey(openId ?? ""),
    queryFn: () => getCancelRequest(openId!),
    enabled: !!openId,
    retry: (count, e) => !(e instanceof ApiError && e.status === 404) && count < 2,
  });
  const summary = openId ? rows.find((r) => r.id === openId) : undefined;
  const shown = detail.data ?? summary;
  // Where the open request was last seen in the list. Once decided it may drop out of the tab, and
  // the request after it moves up into its place: that one is then "Next".
  const [anchor, setAnchor] = useState(-1);
  const found = openId ? rows.findIndex((r) => r.id === openId) : -1;
  const prev = found >= 0 ? rows[found - 1] : anchor > 0 ? rows[anchor - 1] : undefined;
  const next = found >= 0 ? rows[found + 1] : anchor >= 0 ? rows[anchor] : undefined;
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = list;

  const openRequest = useCallback(
    (id: string) => {
      const at = rows.findIndex((r) => r.id === id);
      setAnchor(at);
      // Near the end of what is loaded: fetch the next page, so Next keeps going.
      if (at >= rows.length - 3 && hasNextPage && !isFetchingNextPage) void fetchNextPage();
      setParam({ request: id });
    },
    [rows, hasNextPage, isFetchingNextPage, fetchNextPage, setParam],
  );
  const closeRequest = useCallback(() => {
    setAnchor(-1);
    setParam({ request: null });
  }, [setParam]);
  const showPatient = (patient: string) => {
    search.cancel();
    setAnchor(-1);
    setParam({ patient, status: "all", q: null, request: null });
  };

  const patientName =
    (patientId && rows.find((r) => r.patient_id === patientId)?.patient_name) ||
    (detail.data?.patient_id === patientId ? detail.data.patient_name : null) ||
    "This patient";
  const effectiveSort = sort ?? defaultSort(tab);
  const narrowing = (sort ? 1 : 0) + (patientId ? 1 : 0);
  const activeFilters = narrowing + (q ? 1 : 0);
  const clearFilters = () => {
    search.cancel();
    setParam({ q: null, sort: null, patient: null });
  };

  const empty = activeFilters
    ? "No requests match these filters."
    : tab === "open"
      ? "No requests waiting for a decision."
      : "No requests here yet.";
  const position = found >= 0 && total ? `${found + 1} of ${total.toLocaleString("en-IN")}` : null;

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Cancellation &amp; refund requests</h2>
          <p className="text-sm text-gray-500">
            Paid orders the patient asked to cancel before dispatch. The order is on hold until you decide.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Every request in this tab that the search and filters match, in the list's order. */}
          <ExportButton
            table="refund requests"
            columns={EXPORT_COLUMNS}
            keyOf={(r) => r.id}
            fetchPage={byPageNumber((page) => listCancelRequests({ ...query, page, per_page: EXPORT_PAGE_SIZE }))}
          />
          <button
            type="button"
            onClick={() => setLookupOpen(true)}
            className="rounded-lg border bg-white px-4 py-2 text-sm font-medium shadow-xs transition hover:bg-gray-50"
          >
            Refund an order…
          </button>
        </div>
      </div>

      <StatusTabs
        tab={tab}
        counts={counts.data}
        onChange={(t) => {
          setAnchor(-1);
          setParam({ status: t === "open" ? null : t });
        }}
      />

      <FilterBar
        label="Search and filter requests"
        narrowing={narrowing}
        search={
          <Control label="Search" className="min-w-[200px] flex-1">
            <div className="relative">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="search"
                value={search.text}
                onChange={(e) => search.onChange(e.target.value)}
                maxLength={SEARCH_MAX}
                placeholder="Order reference, patient name or phone…"
                className="w-full rounded-lg border py-1.5 pl-11 pr-2 outline-hidden focus:border-brand focus:ring-1 focus:ring-brand"
              />
            </div>
          </Control>
        }
      >
        <Control label="Order">
          <Select
            value={effectiveSort}
            options={[
              { value: "oldest", label: "Oldest first" },
              { value: "newest", label: "Newest first" },
            ]}
            onChange={(v) => setParam({ sort: v === defaultSort(tab) ? null : v })}
          />
        </Control>
        {patientId && (
          // A div, not a <label>: a click on the name would otherwise press the clear button.
          <div className="flex flex-col gap-1 text-xs text-gray-500">
            Patient
            <span className="inline-flex max-w-56 items-center gap-1 rounded-lg border border-brand bg-brand-50 py-1 pl-2.5 pr-1 text-brand">
              <span className="truncate">{patientName}</span>
              <button
                type="button"
                onClick={() => setParam({ patient: null })}
                aria-label="Show every patient's requests"
                className="grid h-6 w-6 shrink-0 place-items-center rounded-md hover:bg-white"
              >
                ×
              </button>
            </span>
          </div>
        )}
        {activeFilters > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="ml-auto self-end rounded-lg px-3 py-1.5 text-gray-500 hover:bg-gray-100"
          >
            Clear filters ({activeFilters})
          </button>
        )}
      </FilterBar>

      {/* The previous rows stay, dimmed, while a new search or tab loads. */}
      <div
        className={`flex min-h-0 flex-1 flex-col transition-opacity ${list.isPlaceholderData ? "opacity-60" : ""}`}
        aria-busy={list.isPlaceholderData}
      >
        <VirtualTable
          label="Cancellation and refund requests"
          columns={COLUMNS}
          rows={rows}
          rowKey={(r) => r.id}
          rowHeight={ROW_H}
          loading={list.isLoading}
          error={list.isError ? describeError(list.error) : null}
          onRetry={() => void list.refetch()}
          empty={empty}
          total={total}
          hasMore={list.hasNextPage}
          loadingMore={list.isFetchingNextPage}
          loadMoreFailed={list.isFetchNextPageError}
          onLoadMore={list.fetchNextPage}
          resetKey={JSON.stringify(query)}
          renderRow={(r) => <RequestRow r={r} now={now} active={r.id === openId} onOpen={() => openRequest(r.id)} />}
        />
      </div>

      <Modal
        open={!!openId}
        title={shown ? `Request · ${shown.order_ref}` : "Request"}
        size="lg"
        onClose={closeRequest}
      >
        {openId && (
          <>
            <div className="sticky -top-5 z-10 -mx-5 -mt-5 mb-4 flex items-center justify-between gap-2 border-b bg-surface px-5 py-2 text-sm">
              <button
                type="button"
                disabled={!prev}
                onClick={() => prev && openRequest(prev.id)}
                className="rounded-lg px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                ‹ Previous
              </button>
              <span className="text-xs text-gray-400">{position}</span>
              <button
                type="button"
                disabled={!next}
                onClick={() => next && openRequest(next.id)}
                className="rounded-lg px-2 py-1 font-medium text-brand hover:bg-brand-50 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                Next ›
              </button>
            </div>
            <RequestDetail
              key={openId}
              summary={summary}
              detail={detail.data}
              loading={detail.isLoading}
              error={detail.error}
              now={now}
              onOpenRequest={openRequest}
              onShowPatient={showPatient}
            />
          </>
        )}
      </Modal>

      <Modal open={lookupOpen} title="Refund an order" size="lg" onClose={() => setLookupOpen(false)}>
        <OrderLookup />
      </Modal>
    </div>
  );
}

function Control({ label, children, className = "" }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`flex flex-col gap-1 text-xs text-gray-500 ${className}`}>
      {label}
      {children}
    </label>
  );
}

function StatusTabs({
  tab,
  counts,
  onChange,
}: {
  tab: Tab;
  counts: CancelRequestCounts | undefined;
  onChange: (tab: Tab) => void;
}) {
  return (
    <div role="tablist" aria-label="Request status" className="grid shrink-0 grid-cols-2 gap-1 rounded-xl border bg-white p-1 shadow-xs sm:flex">
      {TABS.map((t) => {
        const active = t.key === tab;
        const n = counts?.[t.key];
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            className={`flex shrink-0 items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              active ? "bg-brand text-brand-fg" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            {t.label}
            {n !== undefined && (
              <span
                className={`rounded-full px-1.5 text-xs tabular-nums ${
                  active
                    ? "bg-white/20"
                    : t.key === "open" && n > 0
                      ? "bg-amber-100 text-amber-800"
                      : "bg-gray-100 text-gray-500"
                }`}
              >
                {n.toLocaleString("en-IN")}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- list

// Order and status at every width; when asked from sm, the amounts from md, the reason from lg
// (narrower screens show the age under the patient).
const COLUMNS: TableColumn[] = [
  { key: "request", header: "Order · patient", width: { base: "minmax(0,1fr)", lg: "minmax(0,1.2fr)" } },
  { key: "reason", header: "Reason", width: "minmax(0,1.6fr)", from: "lg" },
  { key: "asked", header: "Asked", width: "8rem", from: "sm" },
  { key: "amount", header: "Paid / refund", width: "8rem", from: "md", align: "end" },
  { key: "status", header: "Status", width: "6.5rem" },
];

function RequestRow({
  r,
  now,
  active,
  onOpen,
}: {
  r: AdminCancelRequest;
  now: number;
  active: boolean;
  onOpen: () => void;
}) {
  const overdue = isOverdue(r, now);
  return (
    <div onClick={onOpen} className={`h-full cursor-pointer transition-colors ${active ? "bg-brand-50" : "hover:bg-gray-50"}`}>
      <TableRow
        columns={COLUMNS}
        className="h-full text-sm"
        cells={{
          request: (
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen();
                  }}
                  className="font-mono font-semibold text-gray-900 hover:text-brand hover:underline"
                >
                  {r.order_ref}
                </button>
                <span className={`truncate text-xs sm:hidden ${overdue ? "text-red-600" : "text-gray-400"}`}>{ago(r.created_at, now)}</span>
              </div>
              <div className="truncate text-xs text-gray-500">
                {r.patient_name || "Patient"}
                {r.patient_phone ? ` · ${phoneLabel(r.patient_phone)}` : ""}
              </div>
            </div>
          ),
          reason: (
            <p className="line-clamp-2 break-words text-xs text-gray-600" title={r.reason}>
              {r.reason}
            </p>
          ),
          asked: (
            <div className="min-w-0 text-xs">
              <div className="truncate text-gray-700">{dateTime(r.created_at)}</div>
              <div className={overdue ? "font-medium text-red-600" : "text-gray-400"}>
                {r.status === "open" ? `waiting ${age(r.created_at, now)}` : ago(r.created_at, now)}
              </div>
            </div>
          ),
          amount: (
            <div className="text-xs tabular-nums">
              <div className="text-gray-700">{formatINR(r.amount_paid)}</div>
              <div className="text-gray-400">
                {r.status === "open"
                  ? `up to ${formatINR(r.refund_max)}`
                  : r.status === "refunded" && r.refund_amount
                    ? `refunded ${formatINR(r.refund_amount)}`
                    : "—"}
              </div>
            </div>
          ),
          status: (
            <div className="flex flex-col gap-0.5">
              <StatusBadge status={r.status} />
              {r.status === "refunded" && r.refund_status && r.refund_status !== "processed" && (
                <span className={`text-xs ${r.refund_status === "failed" ? "text-red-600" : "text-amber-700"}`}>
                  {r.refund_status === "failed" ? "Refund failed" : "Processing"}
                </span>
              )}
            </div>
          ),
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------- one request

function RequestDetail({
  summary,
  detail,
  loading,
  error,
  now,
  onOpenRequest,
  onShowPatient,
}: {
  summary: AdminCancelRequest | undefined;
  detail: AdminCancelRequestDetail | undefined;
  loading: boolean;
  error: unknown;
  now: number;
  onOpenRequest: (id: string) => void;
  onShowPatient: (patientId: string) => void;
}) {
  // The list's row shows at once; the rest fills in when the request's detail arrives.
  const r: AdminCancelRequest | undefined = detail ?? summary;
  if (!r) {
    if (loading) return <Skeleton lines={6} />;
    const missing = error instanceof ApiError && error.status === 404;
    return (
      <p className="text-sm text-red-600">
        {missing ? "This request doesn't exist, or the link is wrong." : error ? describeError(error) : "Couldn't load this request."}
      </p>
    );
  }
  const others = detail?.patient_other_requests_total ?? 0;

  return (
    <div className="space-y-5 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-base font-semibold">{r.order_ref}</span>
            <StatusBadge status={r.status} />
          </div>
          <div className="mt-0.5 text-xs text-gray-500">
            Asked {dateTime(r.created_at)} ({ago(r.created_at, now)}) · order{" "}
            <span className="font-medium text-gray-700">{r.order_status_label || r.order_status_code}</span>
          </div>
        </div>
      </div>

      <Section title="Patient">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium text-gray-800">{r.patient_name || "Patient"}</div>
            {r.patient_phone && (
              <a href={`tel:${r.patient_phone}`} className="text-xs text-brand hover:underline">
                {phoneLabel(r.patient_phone)}
              </a>
            )}
          </div>
          {detail && (
            <div className="text-xs text-gray-500">
              {others === 0 ? (
                "Their first request"
              ) : (
                <button type="button" onClick={() => onShowPatient(r.patient_id)} className="font-medium text-brand hover:underline">
                  All {others + 1} requests from this patient →
                </button>
              )}
            </div>
          )}
        </div>
      </Section>

      <Section title="Why they want to cancel">
        <blockquote className="whitespace-pre-wrap break-words rounded-lg border-l-4 border-amber-300 bg-amber-50/50 px-3 py-2 text-gray-800">
          {r.reason}
        </blockquote>
      </Section>

      <Section title="Money">
        <dl className="grid grid-cols-3 gap-2 rounded-lg border p-3">
          <Money label="Paid" value={r.amount_paid} />
          <Money label="GST in it" value={r.gst_total} />
          <Money
            label={r.status === "open" ? "Refundable now (most)" : r.status === "refunded" ? "Left to refund" : "Refundable (most)"}
            value={r.refund_max}
            strong={r.status === "open"}
          />
        </dl>
      </Section>

      <Section title="Order">{detail ? <OrderSummary d={detail} /> : <Skeleton lines={3} />}</Section>

      <Section title="Decision">
        {r.status === "open" ? <Decide request={r} /> : <Resolution r={r} detail={detail} />}
      </Section>

      {detail && detail.refunds.length > 0 && (
        <Section title="Refunds on this order">
          <RefundList refunds={detail.refunds} />
        </Section>
      )}

      {detail && detail.patient_other_requests.length > 0 && (
        <Section title="This patient's other requests">
          <ul className="divide-y rounded-lg border">
            {detail.patient_other_requests.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() => onOpenRequest(o.id)}
                  className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50"
                >
                  <div className="min-w-0">
                    <div>
                      <span className="font-mono font-medium">{o.order_ref}</span>
                      {o.order_id === detail.order_id && <span className="ml-2 text-xs text-gray-400">this order</span>}
                    </div>
                    <div className="truncate text-xs text-gray-500">{o.reason}</div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-0.5">
                    <StatusBadge status={o.status} />
                    <span className="text-xs text-gray-400">{dateTime(o.created_at)}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {others > detail.patient_other_requests.length && (
            <button
              type="button"
              onClick={() => onShowPatient(r.patient_id)}
              className="mt-2 text-xs font-medium text-brand hover:underline"
            >
              Show all {others + 1} →
            </button>
          )}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">{title}</h4>
      {children}
    </section>
  );
}

function Money({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd className={`tabular-nums ${strong ? "font-semibold text-gray-900" : "text-gray-700"}`}>{formatINR(value)}</dd>
    </div>
  );
}

function Skeleton({ lines }: { lines: number }) {
  return (
    <div className="space-y-2" aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-5 animate-pulse rounded-sm bg-gray-100" />
      ))}
    </div>
  );
}

function OrderSummary({ d }: { d: AdminCancelRequestDetail }) {
  const facts: [string, ReactNode][] = [
    ["Placed", dateTime(d.order_placed_at)],
    ["Paid", d.order_paid_at ? dateTime(d.order_paid_at) : "Not paid"],
    ["Payment ID", d.rzp_payment_id ? <span className="select-all break-all font-mono text-xs">{d.rzp_payment_id}</span> : "—"],
  ];
  if (d.order_cancelled_at) facts.push(["Cancelled", dateTime(d.order_cancelled_at)]);
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
        {facts.map(([label, value]) => (
          <div key={label} className="flex gap-2">
            <dt className="w-24 shrink-0 text-gray-400">{label}</dt>
            <dd className="min-w-0 text-gray-700">{value}</dd>
          </div>
        ))}
      </dl>
      {d.lines.length > 0 && (
        // A long order folds away, so the decision below stays within reach.
        <details open={d.lines.length <= 5} className="rounded-lg border">
          <summary className="cursor-pointer px-3 py-2 text-gray-600">
            {d.lines.length} {d.lines.length === 1 ? "medicine" : "medicines"} · {formatINR(d.medicines_subtotal)}
            {Number(d.delivery_charge) > 0 && ` + ${formatINR(d.delivery_charge)} delivery`}
          </summary>
          <ul className="divide-y border-t">
            {d.lines.map((ln, i) => (
              <li key={i} className={`flex items-center justify-between gap-3 px-3 py-1.5 ${ln.is_available ? "" : "text-gray-400"}`}>
                <span className="min-w-0 truncate">{ln.medicine_name || "Medicine"}</span>
                <span className="shrink-0 text-xs tabular-nums">
                  {ln.is_available ? `Qty ${ln.quantity} · ${formatINR(ln.taxable_value)}` : "Unavailable"}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Resolution({ r, detail }: { r: AdminCancelRequest; detail: AdminCancelRequestDetail | undefined }) {
  const refunded = r.status === "refunded";
  return (
    <div className={`rounded-lg border p-3 ${refunded ? "border-emerald-200 bg-emerald-50/40" : "bg-gray-50"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium text-gray-800">
          {refunded ? "Refunded" : "Declined"}
          {detail?.resolved_by_name ? ` by ${detail.resolved_by_name}` : ""}
          {r.resolved_at && <span className="font-normal text-gray-500"> · {dateTime(r.resolved_at)}</span>}
        </div>
        {refunded && r.refund_amount && (
          <div className="flex items-center gap-2">
            <span className="font-semibold tabular-nums">{formatINR(r.refund_amount)}</span>
            {r.refund_status && <RefundPill status={r.refund_status} />}
          </div>
        )}
      </div>
      {r.resolution_note && (
        <p className="mt-1 whitespace-pre-wrap break-words text-gray-600">
          <span className="text-gray-400">{refunded ? "Notes: " : "Told the patient: "}</span>
          {r.resolution_note}
        </p>
      )}
      {!refunded && <p className="mt-1 text-xs text-gray-500">The order went ahead. The patient may ask again before dispatch.</p>}
    </div>
  );
}

// ---------------------------------------------------------------- deciding

function Decide({ request: r }: { request: AdminCancelRequest }) {
  const [mode, setMode] = useState<"choose" | "refund" | "decline">("choose");
  if (mode === "refund") return <RefundForm request={r} onBack={() => setMode("choose")} />;
  if (mode === "decline") return <DeclineForm request={r} onBack={() => setMode("choose")} />;
  const nothingLeft = Number(r.refund_max) <= 0;
  return (
    <div className="space-y-2">
      {nothingLeft && (
        <p className="rounded-lg bg-amber-50 p-2 text-amber-800">
          Earlier refunds already took everything refundable on this order. Decline the request, or look at the refunds below.
        </p>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          disabled={nothingLeft}
          onClick={() => setMode("refund")}
          className="rounded-lg bg-brand px-4 py-2 font-medium text-brand-fg disabled:opacity-50"
        >
          Cancel order &amp; refund…
        </button>
        <button type="button" onClick={() => setMode("decline")} className="rounded-lg border px-4 py-2">
          Decline…
        </button>
      </div>
    </div>
  );
}

const AMOUNT_RE = /^\d+(\.\d{1,2})?$/;

function amountProblem(amount: string, max: number): string | null {
  const a = amount.trim();
  if (!a) return "Enter the amount to refund.";
  if (!AMOUNT_RE.test(a)) return "Enter rupees, with at most two decimals (e.g. 95.50).";
  const n = Number(a);
  if (n <= 0) return "The refund must be more than zero.";
  if (n > max) return `At most ${formatINR(max)} can be refunded.`;
  return null;
}

function noteProblem(note: string): string | null {
  return note.trim().length < NOTE_MIN ? `Write at least ${NOTE_MIN} characters.` : null;
}

function RefundForm({ request: r, onBack }: { request: AdminCancelRequest; onBack: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState(r.refund_max);
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [touched, setTouched] = useState(false);
  const key = useIdempotencyKey();
  const max = Number(r.refund_max);
  const badAmount = amountProblem(amount, max);
  const badNote = noteProblem(note);
  const less = !badAmount && Number(amount) < max;

  const refund = useMutation({
    mutationFn: async () => {
      const body = { amount: amount.trim(), note: note.trim() };
      const done = await refundCancelRequest(r.id, body, key.keyFor({ id: r.id, ...body }));
      await attachAfterRefund(done.refund_id, file);
      return done;
    },
    onSuccess: (done) => {
      key.reset();
      toast.success(`${r.order_ref} cancelled; refund of ${formatINR(done.refund_amount ?? amount)} started.`);
      // Shown as decided at once; the re-read fills in who decided and the refund's state.
      qc.setQueryData<AdminCancelRequestDetail>(detailKey(r.id), (old) => (old ? { ...old, ...done } : old));
      refreshAfterDecision(qc, r);
    },
    onError: (e) => {
      setConfirming(false);
      if (e instanceof ApiError && e.code === "CANCEL_REQUEST_RESOLVED") refreshAfterDecision(qc, r);
    },
  });

  if (refund.isSuccess) return <p className="rounded-lg bg-emerald-50 p-3 text-emerald-800">Refund started. Updating…</p>;

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (!badAmount && !badNote) setConfirming(true);
      }}
      className="space-y-3 rounded-lg border p-3"
    >
      <fieldset disabled={confirming || refund.isPending} className="space-y-3">
        <label className="flex flex-col gap-1">
          <span className="text-gray-500">Amount in rupees (at most {formatINR(r.refund_max)})</span>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-invalid={touched && !!badAmount}
              className="w-full rounded-lg border px-3 py-2 tabular-nums sm:w-40"
            />
            {amount !== r.refund_max && (
              <button type="button" onClick={() => setAmount(r.refund_max)} className="text-xs text-brand hover:underline">
                Refund the most
              </button>
            )}
          </div>
          {touched && badAmount && <span className="text-xs text-red-600">{badAmount}</span>}
          {less && <span className="text-xs text-amber-700">Less than the most allowed: say why in the notes.</span>}
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-gray-500">Notes (kept with the refund{less ? "; say why it is less" : ""})</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={NOTE_MAX}
            rows={3}
            aria-invalid={touched && !!badNote}
            className="rounded-lg border px-3 py-2"
          />
          <span className="flex justify-between text-xs">
            <span className="text-red-600">{touched && badNote}</span>
            <span className="text-gray-400 tabular-nums">
              {note.length}/{NOTE_MAX}
            </span>
          </span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-gray-500">Reference document (optional)</span>
          <input type="file" accept={REFERENCE_ACCEPT} onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
        </label>
      </fieldset>

      {refund.error && <p className="text-red-600">{refundError(refund.error)}</p>}

      {confirming ? (
        <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <p className="text-amber-900">
            Cancel <b>{r.order_ref}</b> and refund <b>{formatINR(amount)}</b> to {r.patient_name || "the patient"}? The
            refund goes back to the original payment and can't be undone.
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <button type="button" disabled={refund.isPending} onClick={() => setConfirming(false)} className="rounded-lg border bg-white px-4 py-2">
              Change
            </button>
            <button
              type="button"
              disabled={refund.isPending}
              onClick={() => refund.mutate()}
              className="rounded-lg bg-brand px-4 py-2 font-medium text-brand-fg disabled:opacity-50"
            >
              {refund.isPending ? "Refunding…" : "Yes, cancel & refund"}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <button type="button" onClick={onBack} className="rounded-lg border px-4 py-2">
            Back
          </button>
          <button className="rounded-lg bg-brand px-4 py-2 font-medium text-brand-fg">Review refund</button>
        </div>
      )}
    </form>
  );
}

function DeclineForm({ request: r, onBack }: { request: AdminCancelRequest; onBack: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);
  const badNote = noteProblem(note);
  const decline = useMutation({
    mutationFn: () => declineCancelRequest(r.id, note.trim()),
    onSuccess: (done) => {
      toast.success(`Declined; ${r.order_ref} goes ahead.`);
      qc.setQueryData<AdminCancelRequestDetail>(detailKey(r.id), (old) => (old ? { ...old, ...done } : old));
      refreshAfterDecision(qc, r);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "CANCEL_REQUEST_RESOLVED") refreshAfterDecision(qc, r);
    },
  });
  if (decline.isSuccess) return <p className="rounded-lg bg-gray-50 p-3 text-gray-700">Declined. Updating…</p>;

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (!badNote) decline.mutate();
      }}
      className="space-y-3 rounded-lg border p-3"
    >
      <label className="flex flex-col gap-1">
        <span className="text-gray-500">Why (the patient sees this; the order goes ahead)</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={NOTE_MAX}
          rows={3}
          disabled={decline.isPending}
          aria-invalid={touched && !!badNote}
          className="rounded-lg border px-3 py-2"
        />
        <span className="flex justify-between text-xs">
          <span className="text-red-600">{touched && badNote}</span>
          <span className="text-gray-400 tabular-nums">
            {note.length}/{NOTE_MAX}
          </span>
        </span>
      </label>
      {decline.error && <p className="text-red-600">{refundError(decline.error)}</p>}
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        <button type="button" onClick={onBack} disabled={decline.isPending} className="rounded-lg border px-4 py-2">
          Back
        </button>
        <button disabled={decline.isPending} className="rounded-lg border border-red-300 px-4 py-2 text-red-700 disabled:opacity-50">
          {decline.isPending ? "Declining…" : "Decline request"}
        </button>
      </div>
    </form>
  );
}

function RefundList({ refunds }: { refunds: Refund[] }) {
  return (
    <ul className="divide-y rounded-lg border text-sm">
      {refunds.map((r) => (
        <li key={r.id} className="flex flex-wrap items-start justify-between gap-2 px-3 py-2">
          <div className="min-w-0">
            <div className="font-medium tabular-nums">
              {formatINR(r.requested_amount)}
              <span className="ml-2 text-xs font-normal text-gray-400">{dateTime(r.initiated_at)}</span>
            </div>
            <div className="break-words text-xs text-gray-500">{r.reason}</div>
            {r.failure_reason && <div className="break-words text-xs text-red-600">{r.failure_reason}</div>}
            {r.reference_name && (
              <button
                type="button"
                onClick={() => downloadRefundReference(r).catch((e) => toast.error(describeError(e)))}
                className="text-xs text-brand hover:underline"
              >
                {r.reference_name}
              </button>
            )}
          </div>
          <RefundPill status={r.status} />
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- order lookup

function OrderLookup() {
  const [term, setTerm] = useState("");
  const [submitted, setSubmitted] = useState<string | null>(null);
  // By the reference the patient quotes, or the ID (ADM-11): only the internal ID was taken, and
  // "ORD-12" was answered "Could not load this order."
  const { data: order, isFetching, error } = useQuery({
    queryKey: ["admin-order-lookup", submitted],
    queryFn: () => lookupOrder(submitted!),
    enabled: !!submitted,
    retry: false,
  });

  return (
    <div className="space-y-4 text-sm">
      <p className="text-gray-500">Look up an order — e.g. a payment captured after it was cancelled — and refund it.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(term.trim() || null);
        }}
        className="flex flex-col gap-2 sm:flex-row"
      >
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Order reference, e.g. ORD-12"
          className="min-w-0 flex-1 rounded-lg border px-3 py-2"
        />
        <button className="rounded-lg bg-brand px-4 py-2 font-medium text-brand-fg">Look up</button>
      </form>
      {isFetching && <p className="text-gray-400">Looking up…</p>}
      {error && <p className="text-red-600">{describeError(error)}</p>}
      {order && !isFetching && (
        <>
          <div className="rounded-xl border bg-white p-4 shadow-xs">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono font-semibold">{order.order_id}</span>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{order.status_label}</span>
            </div>
            <div className="mt-1 text-gray-600">
              {order.patient_name ?? "Patient"}
              {order.patient_phone && ` · ${phoneLabel(order.patient_phone)}`} · {formatINR(order.final_amount)}
              {order.paid_at ? ` · paid ${new Date(order.paid_at).toLocaleDateString()}` : " · not paid"}
              {order.cancelled_at && ` · cancelled ${new Date(order.cancelled_at).toLocaleDateString()}`}
            </div>
          </div>
          <RefundPanel orderId={order.id} />
        </>
      )}
    </div>
  );
}

function RefundPanel({ orderId }: { orderId: string }) {
  const qc = useQueryClient();
  const { data: refunds, isLoading, isError, error } = useQuery({
    queryKey: ["admin-refunds", orderId],
    queryFn: () => listRefunds(orderId),
    retry: false,
  });

  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const refundKey = useIdempotencyKey();
  const initiate = useMutation({
    mutationFn: async () => {
      const body = { amount: amount.trim() || undefined, reason };
      const refund: Refund = await initiateRefund(orderId, body, refundKey.keyFor({ orderId, ...body }));
      await attachAfterRefund(refund.id, file);
      return refund;
    },
    onSuccess: () => {
      refundKey.reset();
      setAmount("");
      setReason("");
      setFile(null);
      void qc.invalidateQueries({ queryKey: ["admin-refunds", orderId] });
      // The order may have an open request; its refundable amount has changed.
      void qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });

  if (isLoading) return <p className="text-gray-400">Loading…</p>;
  if (isError) {
    const notFound = error instanceof ApiError && error.status === 404;
    return <p className="text-red-600">{notFound ? "Order not found." : "Could not load this order."}</p>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-white p-4 shadow-xs">
        <h3 className="mb-3 font-medium">Initiate refund</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            initiate.mutate();
          }}
          className="space-y-3"
        >
          <label className="flex flex-col gap-1">
            <span className="text-gray-500">Amount in rupees (blank = the most allowed: amount paid less GST, less the retained share)</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 sm:w-48"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-gray-500">Reason (required)</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} required className="rounded-lg border px-3 py-2" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-gray-500">Reference document (optional)</span>
            <input type="file" accept={REFERENCE_ACCEPT} onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
          </label>
          {initiate.error && <p className="text-red-600">{refundError(initiate.error)}</p>}
          <button
            disabled={initiate.isPending || !reason.trim()}
            className="rounded-lg bg-brand px-4 py-2 font-medium text-brand-fg disabled:opacity-50"
          >
            {initiate.isPending ? "Initiating…" : "Initiate refund"}
          </button>
        </form>
      </div>

      <div>
        <h3 className="mb-2 font-medium text-gray-500">Refund history</h3>
        {refunds && refunds.length > 0 ? <RefundList refunds={refunds} /> : <p className="text-gray-400">No refunds yet.</p>}
      </div>
    </div>
  );
}
